import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { 
  Check, 
  Star, 
  AutoAwesome, 
  Bolt, 
  Shield, 
  Support,
  Code,
  Speed
} from '@mui/icons-material';
import { useAxios } from '../context/AxiosContext';

const Pricing = () => {
  const { isAuthenticated } = useAxios();

  const features = [
    { icon: <Check />, text: 'Discord chat widget integration' },
    { icon: <Check />, text: 'Real-time message synchronization' },
    { icon: <Check />, text: 'Guest messaging' },
    { icon: <Check />, text: 'Secure authentication' },
    { icon: <Check />, text: 'Email support' },
    { icon: <Check />, text: 'Regular updates' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ pt: 8, pb: 6, textAlign: 'center' }}>
          <Chip
            label="CLOSED BETA"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              fontWeight: 'bold',
              mb: 3,
              fontSize: '0.9rem',
              px: 2,
            }}
          />
          <Typography
            component="h1"
            variant="h2"
            sx={{
              fontWeight: 'bold',
              mb: 2,
              background: 'linear-gradient(45deg, #5865F2 30%, #57F287 90%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            🧙 Magical Pricing
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'text.secondary', mb: 4, maxWidth: 600, mx: 'auto' }}
          >
            Join our exclusive closed beta and unlock the power of Discord integration for your website
          </Typography>
        </Box>

        {/* Pricing Card */}
        <Grid container justifyContent="center" sx={{ mb: 8 }}>
          <Grid item xs={12} md={8} lg={6}>
            <Card
              sx={{
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 3,
                position: 'relative',
                overflow: 'visible',
              }}
            >
              {/* Beta Badge */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'secondary.main',
                  color: 'black',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Star sx={{ fontSize: 20 }} />
                BETA SPECIAL
              </Box>

              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Wizcord Pro
                </Typography>
                
                {/* Beta Pricing */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ 
                      color: 'secondary.main', 
                      fontWeight: 'bold',
                      mb: 1 
                    }}
                  >
                    First 100 Users Only
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                    <Typography
                      variant="h2"
                      sx={{ 
                        fontWeight: 'bold',
                        color: 'secondary.main',
                      }}
                    >
                      FREE
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                      for 2 months
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                    Then just
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 'bold' }}
                    >
                      $14.99
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                      /month
                    </Typography>
                  </Box>
                </Box>

                {/* Features List */}
                <Box sx={{ textAlign: 'left', mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
                    Everything you need to get started:
                  </Typography>
                  <List dense>
                    {features.map((feature, index) => (
                      <ListItem key={index} disableGutters>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Box
                            sx={{
                              color: 'secondary.main',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {feature.icon}
                          </Box>
                        </ListItemIcon>
                        <ListItemText primary={feature.text} />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                {/* CTA Button */}
                <Button
                  component={Link}
                  to={isAuthenticated ? "/dashboard" : "/register"}
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'black',
                    fontWeight: 'bold',
                    py: 1.5,
                    px: 4,
                    fontSize: '1.1rem',
                    '&:hover': {
                      bgcolor: 'secondary.dark',
                    },
                    width: '100%',
                    maxWidth: 300,
                    mb: 2,
                  }}
                >
                  {isAuthenticated ? 'Go to Dashboard' : 'Join Closed Beta'}
                </Button>

                {/* Documentation Link */}
                <Button
                  component={Link}
                  to="/docs"
                  variant="outlined"
                  size="medium"
                  onClick={() => {
                    // Scroll to top when navigating to docs
                    setTimeout(() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }, 100);
                  }}
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    fontWeight: 'bold',
                    py: 1,
                    px: 3,
                    '&:hover': {
                      borderColor: 'primary.dark',
                      bgcolor: 'rgba(88, 101, 242, 0.04)',
                    },
                    width: '100%',
                    maxWidth: 300,
                  }}
                >
                  📚 View Documentation
                </Button>
                
                <Typography
                  variant="body2"
                  sx={{ 
                    color: 'text.secondary',
                    mt: 2,
                    fontStyle: 'italic'
                  }}
                >
                  Limited spots available • No credit card required
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* FAQ Section */}
        <Box sx={{ pb: 8 }}>
          <Typography
            variant="h4"
            sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold' }}
          >
            Frequently Asked Questions
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'background.paper', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  What happens after the 2-month free period?
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Your service will automatically continue at $14.99/month. You can cancel anytime before the free period ends if you decide it's not for you.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'background.paper', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Is there really no credit card required?
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Correct! Join the beta with just your email. We'll only ask for payment details before your free period expires.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'background.paper', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  What makes this different from other Discord bots?
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Wizcord isn't just a bot - it's a complete integration solution that lets you embed Discord chat directly on your website with full customization.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'background.paper', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Can I cancel anytime?
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Yes! No long-term contracts or commitments. Cancel your subscription anytime from your dashboard.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Pricing; 